import { Gem } from "lucide-react";
import { useTranslation } from "react-i18next";

export interface PackageVolumePointsProps {
  /** `volumePoints` del paquete. Del servidor, ya resuelto. */
  points: number;
  /** Cuántas piezas trae en total, para acompañar al dato. */
  units: number;
}

/**
 * Los volume points que otorga el paquete, y cuántas piezas trae.
 *
 * ── Por qué el número no se suma aquí ────────────────────────────────
 * `volumePoints` llega calculado y CONGELADO del servidor: el contrato
 * dice que se guarda ya resuelto para que el día que un producto cambie
 * de puntos, los paquetes ya vendidos no cambien de valor
 * retroactivamente. Sumarlo en el navegador a partir del catálogo actual
 * daría justo el número que el contrato quiere evitar.
 *
 * ── VP se explica, no se asume ───────────────────────────────────────
 * "VP" a secas no le dice nada a quien entra por primera vez, y esta es
 * una pantalla pública. El número va grande y la sigla debajo, con su
 * nombre completo.
 */
export function PackageVolumePoints({ points, units }: PackageVolumePointsProps) {
  const { t } = useTranslation();

  // Un paquete de 0 puntos es de membresía: ni puntos ni comisión. Se
  // dice con todas sus letras en vez de presumir «0 volume points».
  if (points <= 0) {
    return (
      <div className="rounded-ds-md bg-amber-50 px-4 py-3 text-[14px] font-semibold text-amber-800">
        {t("No comisionable y no puntos")}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 rounded-ds-md bg-ds-mist/60 px-4 py-3">
      <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-white text-ds-violet-pop">
        <Gem className="h-4 w-4" aria-hidden="true" />
      </span>

      <div className="min-w-0">
        <p className="text-[14px] font-semibold leading-tight text-ds-ink">
          {t("{{n}} volume points", { n: points })}
        </p>
        <p className="mt-0.5 text-[12px] leading-tight text-ds-ink-3">
          {units === 1
            ? t("{{n}} pieza al mes", { n: units })
            : t("{{n}} piezas al mes", { n: units })}
        </p>
      </div>
    </div>
  );
}
